<template>
  <div class="ExtractTemplates">
    <v-row class="ExtractTemplates__filter-row">
      <v-col>
        <v-btn text small color="primary" @click="isDialogOpen = true">
          + Add Extract Template
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
        class="ExtractTemplates__data-table elevation-0"
        outlined
        item-key="id"
        :item-class="() => 'ExtractTemplates__data-table-row'"
        :items="Templates"
        :headers="headers"
        :loading="fetchTemplatesLoading || deleteTemplateLoading"
        @click:row="handleGoToTemplate"
    >
      <template v-slot:[`item.scope`]="{ item }">
        <v-chip small :color="item.customer_id ? 'blue' : 'green'" dark>
          <strong>
            {{ item.customerId !== null ? 'Customer' : 'System' }}
          </strong>
        </v-chip>
      </template>

      <template v-slot:[`item.description`]="{ item }">
        <div class="description-truncated">
          {{ item.description }}
        </div>
      </template>

      <template v-slot:[`item.schema`]="{ item }">
        <span class="text-caption">
          {{ getSchemaPreview(item.schema) }}
        </span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          icon
          small
          color="primary"
          @click.stop="editTemplate(item)"
          class="mr-1"
          :disabled="isSystemTemplate(item)"
          :title="isSystemTemplate(item) ? 'System templates cannot be edited' : 'Edit template'"
        >
          <v-icon small> mdi-pencil </v-icon>
        </v-btn>
        <v-btn icon small color="secondary" @click.stop="duplicateTemplate(item)" class="mr-1">
          <v-icon small> mdi-content-copy </v-icon>
        </v-btn>
        <v-btn
          icon
          color="error"
          @click.stop="triggerDelete(item)"
          :disabled="isSystemTemplate(item)"
          :title="isSystemTemplate(item) ? 'System templates cannot be deleted' : 'Delete template'"
        >
          <v-icon small> mdi-delete </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <action-prompt
        v-model="showDeleteTemplateModal"
        v-if="showDeleteTemplateModal"
        action-text="Delete"
        :action="removeTemplate"
        :loading="deleteTemplateLoading"
        header="Delete Template"
        :body="deleteMessageBody"
        action-type="error"
    />

    <ExtractTemplateModal
        :dialog="isDialogOpen"
        :mode="'database'"
        :template="editingTemplate"
        @close="closeDialog"
        @save="handleSave"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import ActionPrompt from '@/components/modals/action-prompt/action-prompt.vue';
import ExtractTemplateModal from '@/components/connector-settings/ai-service/ExtractTemplateModal.vue';

const { mapGetters: templatesGetters, mapActions: templatesActions } =
    createNamespacedHelpers('extractTemplates');
const { mapGetters: customerGetters } = createNamespacedHelpers('customer');

export default {
  name: 'ExtractTemplates',
  data() {
    return {
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Scope', value: 'scope' },
        { text: 'Schema', value: 'schema' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      templateId: null,
      showDeleteTemplateModal: false,
      isDialogOpen: false,
      editingTemplate: null,
    };
  },
  components: {
    ActionPrompt,
    ExtractTemplateModal
  },
  watch: {
    'selectedCustomer.customer_id'() {
      this.fetchTemplates();
    },
  },
  computed: {
    ...templatesGetters({
      Templates: 'TEMPLATES',
      fetchTemplatesLoading: 'FETCH_TEMPLATES_LOADING',
      deleteTemplateLoading: 'DELETE_TEMPLATE_LOADING',
    }),
    ...customerGetters({
      selectedCustomer: 'SELECTED_CUSTOMER',
    }),
    deleteMessageBody() {
      return 'Are you sure you want to delete this Extract Template?';
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    ...templatesActions(['fetchTemplates', 'deleteTemplate', 'createTemplate']),
    triggerDelete({ id }) {
      this.templateId = id;
      this.showDeleteTemplateModal = true;
    },
    async removeTemplate() {
      await this.deleteTemplate(this.templateId);
      this.templateId = null;
      this.showDeleteTemplateModal = false;
    },
    editTemplate(item) {
      this.$router.push({ path: `/extract-templates/${item.id}` });
    },
    handleGoToTemplate(item) {
      this.$router.push({ path: `/extract-templates/${item.id}` });
    },
    duplicateTemplate(template) {
      const templateCopy = {
        name: `${template.name} (Copy)`,
        description: template.description,
        schema: JSON.parse(JSON.stringify(template.schema)), // Deep copy
      };
      this.editingTemplate = templateCopy;
      this.isDialogOpen = true;
    },
    closeDialog() {
      this.isDialogOpen = false;
      this.editingTemplate = null;
      this.fetchTemplates();
    },
    async handleSave(templateData) {
      try {
        await this.createTemplate(templateData);
        this.closeDialog();
      } catch (error) {
        console.error('Error creating template:', error);
      }
    },
    isSystemTemplate(template) {
      return template.customerId === null;
    },
    getSchemaPreview(schema) {
      if (!schema || !schema.properties) return 'No fields';
      const fieldCount = Object.keys(schema.properties).length;
      const fieldNames = Object.keys(schema.properties).slice(0, 3).join(', ');
      return fieldCount <= 3 ? fieldNames : `${fieldNames}... (${fieldCount} fields)`;
    },
  },
};
</script>

<style lang="scss" scoped>
.ExtractTemplates {
  &__filter-row {
    margin-bottom: 16px;
  }

  &__data-table {
    background: white;

    &-row {
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
    }
  }
}

.description-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  max-width: 300px;
  font-size: 0.875rem;
  color: #666;
}
</style>